import { useEffect, useState } from 'react'
import { displayTitle } from '../../lib/displayName'
import { styled } from '@linaria/react'
import { MinimalAlert, MinimalButton, MinimalCard, MinimalDropdown, MinimalInput } from '@ovasabi/ui-minimal'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import type { DishReview, ListDishReviewsResponse } from '../../types/protos/menu/v1/menu'
import { refreshPublicCatalogue } from '../../lib/discover'
import { commandPayload, commands } from '../../lib/runtime'
import { formatRFC3339UTC } from '../../lib/timestamp'
import { summarizeReviews } from './ratingSummary'

const Panel = styled.section`
  display: grid;
  gap: ${minimalVars.space.sm};
  form { display: grid; gap: ${minimalVars.space.sm}; }
  p, h3 { margin: 0; }
  ul { margin: 0; padding-left: ${minimalVars.space.lg}; }
`
const Breakdown = styled.div`
  display: grid;
  gap: 6px;
  max-width: 420px;
`
const Bar = styled.div`
  display: grid;
  grid-template-columns: 3.2em minmax(0, 1fr) 2.2em;
  align-items: center;
  gap: ${minimalVars.space.xs};
  font-size: ${minimalVars.typography.captionSize};
  color: ${minimalVars.color.textSecondary};
  font-variant-numeric: tabular-nums;
  > span:last-child { text-align: right; }
`
const Track = styled.span`
  display: block;
  height: 8px;
  border-radius: ${minimalVars.radius.pill};
  background: ${minimalVars.color.bgSurfaceAlt};
  overflow: hidden;
  > span { display: block; height: 100%; border-radius: inherit; background: ${minimalVars.color.brand}; }
`
const Facts = styled.p`
  color: ${minimalVars.color.textSecondary};
  font-size: ${minimalVars.typography.captionSize};
`
const ReviewList = styled.ul`
  list-style: none;
  padding: 0 !important;
  display: grid;
  gap: ${minimalVars.space.xs};
  li { display: grid; gap: 2px; padding-bottom: ${minimalVars.space.xs}; border-bottom: 1px solid ${minimalVars.color.borderSubtle}; }
  li:last-child { border-bottom: 0; }
  small { color: ${minimalVars.color.textTertiary}; }
`
const stars = (value: number) => '★'.repeat(value) + '☆'.repeat(Math.max(0, 5 - value))
const reviewDate = (raw: string) => {
  const iso = formatRFC3339UTC(raw)
  const date = iso ? new Date(iso) : null
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : ''
}

const ratings = [1, 2, 3, 4, 5].map(value => ({ value: String(value), label: `${value} ${value === 1 ? 'star' : 'stars'}` }))
const optionalRatings = [{ value: '0', label: 'Not rated' }, ...ratings]
const message = (error: unknown): string => error instanceof Error ? error.message : 'Could not load reviews. Please try again.'

/** The server determines eligibility and returns the full aggregate. */
export const DishReviews = ({ dishId, profileId, name }: { dishId: string; profileId: string; name: string }) => {
  const [data, setData] = useState<ListDishReviewsResponse | null>(null)
  const [error, setError] = useState('')
  const [reload, setReload] = useState(0)
  const [rating, setRating] = useState('')
  const [food, setFood] = useState('0')
  const [packaging, setPackaging] = useState('0')
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  useEffect(() => {
    let active = true
    setData(null)
    setError('')
    void commands.menu.listDishReviews(dishId).then(response => {
      const raw = commandPayload(response)
      if (!Array.isArray(raw.reviews)) throw new Error('We couldn’t load all the reviews. Try again.')
      const result: ListDishReviewsResponse = { rating: Number(raw.rating), reviewCount: Number(raw.review_count), canReview: raw.can_review === true,
        reviews: raw.reviews.map((value: unknown) => {
          const row = value as Record<string, unknown>
          return { id: String(row.id), dishId: String(row.dish_id), authorProfileId: String(row.author_profile_id),
            rating: Number(row.rating), foodQualityRating: Number(row.food_quality_rating || 0), packagingRating: Number(row.packaging_rating || 0),
            body: String(row.body || ''), createdAt: String(row.created_at || '') }
        }) }
      if (!Number.isFinite(result.rating) || result.rating < 0 || result.rating > 5 || !Number.isInteger(result.reviewCount) || result.reviewCount < 0) throw new Error('We couldn’t load all the reviews. Try again.')
      if (!active) return
      setData(result)
      const mine = result.reviews.find(review => review.authorProfileId === profileId)
      setRating(mine ? String(mine.rating) : '')
      setFood(String(mine?.foodQualityRating || 0))
      setPackaging(String(mine?.packagingRating || 0))
      setBody(mine?.body || '')
    }).catch(error => { if (active) setError(message(error)) })
    return () => { active = false }
  }, [dishId, profileId, reload])
  const submit = async () => {
    setBusy(true)
    setError('')
    setSaved(false)
    try {
      commandPayload(await commands.menu.addDishReview(dishId, { author_profile_id: profileId,
        rating: Number(rating), food_quality_rating: Number(food), packaging_rating: Number(packaging), body: body.trim() }))
      refreshPublicCatalogue()
      setSaved(true)
      setReload(value => value + 1)
    } catch (error) { setError(message(error)) } finally { setBusy(false) }
  }
  return <MinimalCard><Panel aria-label={`Reviews for ${name}`}>
    <h3>{name} · reviews</h3>
    {saved && <MinimalAlert tone="success" title="Review saved">If you rate it again, your new rating replaces the old one.</MinimalAlert>}
    {error && <MinimalAlert tone="danger" title="Reviews unavailable">{error}<MinimalButton variant="secondary" onClick={() => setReload(value => value + 1)}>Retry reviews</MinimalButton></MinimalAlert>}
    {!data && !error && <p role="status">Loading reviews…</p>}
    {data && <>
      <p role="status">{data.reviewCount > 0 ? `${data.rating.toFixed(1)} / 5 · ${data.reviewCount} ${data.reviewCount === 1 ? 'review' : 'reviews'}` : 'No reviews yet'}</p>
      {data.reviewCount > 0 && (() => {
        const summary = summarizeReviews(data.reviews)
        return <>
          <Breakdown aria-label="Ratings by star">
            {summary.distribution.map((row) => (
              <Bar key={row.stars} aria-label={`${row.stars} stars: ${row.count}`}>
                <span>{row.stars} ★</span>
                <Track><span style={{ width: `${Math.round(row.share * 100)}%` }} /></Track>
                <span>{row.count}</span>
              </Bar>
            ))}
          </Breakdown>
          {(summary.foodQuality || summary.packaging) && <Facts>
            {summary.foodQuality && `Food quality ${summary.foodQuality.average.toFixed(1)} / 5`}
            {summary.foodQuality && summary.packaging && ' · '}
            {summary.packaging && `Packaging ${summary.packaging.average.toFixed(1)} / 5`}
          </Facts>}
          {summary.counted < data.reviewCount && <Facts>Breakdown of the latest {summary.counted} of {data.reviewCount} reviews.</Facts>}
        </>
      })()}
      <Facts>Reviews come only from people who ordered this dish. Each person counts once.</Facts>
      <ReviewList>{data.reviews.map((review: DishReview) => <li key={review.id}>
        <span aria-label={`${review.rating} out of 5`}>{stars(review.rating)}</span>
        {review.body && <span>{review.body}</span>}
        <small>{review.authorProfileId === profileId ? 'You' : 'Verified customer'}{reviewDate(review.createdAt) ? ` · ${reviewDate(review.createdAt)}` : ''}</small>
      </li>)}</ReviewList>
      {data.canReview ? <form onSubmit={event => { event.preventDefault(); void submit() }}>
        <MinimalDropdown label={<>Dish rating <span aria-hidden="true">*</span></>} options={ratings} value={rating} onChange={setRating} />
        <MinimalDropdown label="Food quality (optional)" options={optionalRatings} value={food} onChange={setFood} />
        <MinimalDropdown label="Packaging (optional)" options={optionalRatings} value={packaging} onChange={setPackaging} />
        <MinimalInput label="Your review (optional)" maxLength={2000} value={body} onChange={event => setBody(event.target.value)} />
        <MinimalButton type="submit" disabled={!rating || busy} loading={busy}>Save review</MinimalButton>
      </form> : <Facts>You can rate this dish after an order with it is completed.</Facts>}
    </>}
  </Panel></MinimalCard>
}

/** Completed orders retain a review entry point when dishes leave the public catalogue. */
export const OrderDishReviews = ({ orderId, profileId }: { orderId: string; profileId: string }) => {
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<{ id: string; name: string }[] | null>(null)
  const [error, setError] = useState('')
  const [reload, setReload] = useState(0)
  useEffect(() => {
    if (!open) return
    let active = true
    setItems(null)
    setError('')
    void commands.marketplace.getOrder(orderId).then(response => {
      const result = commandPayload(response)
      if (!Array.isArray(result.items)) throw new Error('We couldn’t load the dishes in this order. Try again.')
      const dishes = new Map<string, { id: string; name: string }>()
      for (const item of result.items.slice(0, 200) as Record<string, unknown>[]) {
        if (typeof item.item_ref === 'string' && item.item_ref) dishes.set(item.item_ref, { id: item.item_ref, name: displayTitle(item.item_name) || 'Dish' })
      }
      if (active) setItems([...dishes.values()])
    }).catch(error => { if (active) setError(message(error)) })
    return () => { active = false }
  }, [open, orderId, reload])
  return <Panel>
    <p>How was your meal? Your rating helps other customers choose and counts toward the kitchen rating.</p>
    <MinimalButton variant="secondary" size="sm" aria-expanded={open} onClick={() => setOpen(value => !value)}>{open ? 'Close dish reviews' : 'Rate dishes'}</MinimalButton>
    {open && <>
      {error && <MinimalAlert tone="danger" title="Order reviews unavailable">{error}<MinimalButton onClick={() => setReload(value => value + 1)}>Retry order items</MinimalButton></MinimalAlert>}
      {!items && !error && <p role="status">Loading order dishes…</p>}
      {items?.length === 0 && <p>No dish items are available for review.</p>}
      {items?.map(item => <DishReviews key={item.id} dishId={item.id} name={item.name} profileId={profileId} />)}
    </>}
  </Panel>
}
